import { OrderSection } from '../order-section/order-section';
import React, { useEffect, useState } from 'react';
import { useAPI } from '../../../clients/quipli-service/quipli-service-client';
import { Card } from '../../../components/card/card';
import { DateTime } from 'luxon';
import { Button } from '../../../components/form/button';
import { EmailArchive, SearchEmailArchivesRequest } from '../../../clients/quipli-service/email/email';
import { toast } from '../../../util/toasts';
import { Dropdown } from '../../../components/dropdown/dropdown';
import Icon from '../../../components/icons/icon';
import { Typography } from '../../../components/typography/typography';
import classNames from 'classnames';
import { Paginator } from '../../../components/paginator/paginator-v2';
import { Pagination } from '../../../clients/quipli-service/pagination/pagination';
import { Modal } from '../../../components/modal/modal';

export const EmailHistory = ({
  orderId,
  orderType = 'Order',
  isQuote = false,
  orderDateAdded,
  className,
}: {
  orderId: string;
  orderType: 'Order' | 'WorkOrder';
  isQuote?: boolean;
  orderDateAdded: DateTime;
  className?: string;
}) => {
  const { searchEmailArchives, resendEmail, getConfiguration, sendQuote } = useAPI();
  const [emailArchives, setEmailArchives] = useState<Pagination<EmailArchive>>();
  const [isResendModalOpen, setIsResendModalOpen] = useState<boolean>(false);
  const [isResending, setIsResending] = useState<boolean>(false);
  const [selectedEmail, setSelectedEmail] = useState<EmailArchive>();
  const [expandedId, setExpandedId] = useState<string>();
  const [recipient, setRecipient] = useState<string>('');
  const [timezone, setTimezone] = useState<string>();

  const [searchRequest, setSearchRequest] = useState<SearchEmailArchivesRequest>({
    pageSize: 10,
    pageNumber: 1,
    sortBy: 'sentAt',
    sortDirection: 'desc',
    dateRange: { start: orderDateAdded ?? DateTime.now().minus({ year: 1 }), end: DateTime.now() },
    includeEmailBody: true,
    orderId: orderType == 'Order' ? parseInt(orderId) : undefined,
    workOrderId: orderType == 'WorkOrder' ? orderId : undefined,
  });

  async function search(sortBy?: 'emailTemplateName' | 'sentAt' | 'receiver') {
    if (sortBy) {
      setSearchRequest((req) => {
        let sortDirection: 'asc' | 'desc' = 'asc';
        if (req.sortBy === sortBy) {
          sortDirection = req.sortDirection === 'asc' ? 'desc' : 'asc';
        }
        return {
          ...req,
          sortBy: sortBy || req.sortBy,
          sortDirection,
        };
      });
    } else if (searchRequest.pageSize) {
      await searchEmailArchives(searchRequest).then((res) => setEmailArchives(res));
    }
  }

  useEffect(() => {
    getConfiguration('CONF_TIMEZONE').then((res) => setTimezone(res?.value));
  }, []);

  useEffect(() => {
    search();
  }, [searchRequest]);

  async function resend(selectedEmail: EmailArchive, recipient: string): Promise<void> {
    setIsResending(true);
    try {
      await resendEmail(selectedEmail.id, recipient);
      toast('Email resent', 'success');
      await search();
      setIsResendModalOpen(false);
      setRecipient('');
    } catch (err: any) {
      console.error(err);
      toast('Error resending email', 'error');
    } finally {
      setIsResending(false);
    }
  }

  async function resendQuote() {
    setIsResending(true);
    try {
      await sendQuote(parseInt(orderId));
      toast('Quote resent', 'success');
      setSearchRequest({ ...searchRequest, sortBy: 'sentAt', sortDirection: 'desc' });
    } catch (err: any) {
      console.error(err);
      toast('Error resending quote', 'error');
    } finally {
      setIsResending(false);
    }
  }

  const renderBody = (body: string) => {
    return <div dangerouslySetInnerHTML={{ __html: body }} />;
  };

  return (
    <>
      <OrderSection
        className={classNames('order-emails flex flex-column gap-2', className)}
        header={
          <div className="flex justify-between items-center">
            <Typography type="text-md" weight="medium">
              Email History
            </Typography>
            {orderType === 'Order' && isQuote && (
              <Button className="button-primary" onClick={() => resendQuote()} disabled={isResending}>
                Resend Quote
              </Button>
            )}
          </div>
        }
      >
        <div className="email-history-table">
          <table className="w-full">
            <thead>
              <tr>
                <th className="text-left :hover:cursor-pointer" onClick={() => search('emailTemplateName')}>
                  Email Type<i className="ml-2 fa-solid fa-caret-down"></i>
                </th>
                <th className="text-left :hover:cursor-pointer" onClick={() => search('sentAt')}>
                  Date Sent<i className="ml-2 fa-solid fa-caret-down"></i>
                </th>
                <th className="text-left :hover:cursor-pointer" onClick={() => search('receiver')}>
                  Recipient<i className="ml-2 fa-solid fa-caret-down"></i>
                </th>
                <th />
              </tr>
            </thead>
            <tbody>
              {emailArchives?.items.map((email) => {
                return (
                  <React.Fragment key={email.id}>
                    <tr
                      className={classNames('email-archive-row', { expanded: expandedId === email.id })}
                      onClick={() => setExpandedId((emailId) => (emailId === email.id ? undefined : email.id))}
                    >
                      <td className="text-left">{email.displayName}</td>
                      <td className="text-left">
                        {email.sentAt.setZone(timezone).toLocaleString(DateTime.DATETIME_MED)}
                      </td>
                      <td className="text-left">{email.receiver}</td>
                      <td className="text-right flex justify-end" onClick={() => 0}>
                        <div>
                          <Dropdown
                            placement="bottom-end"
                            trigger={() => (
                              <Button className="button-tertiary" data-cy={`email_archive_menu`}>
                                <Icon icon="DotsHorizontal" />
                              </Button>
                            )}
                          >
                            <div
                              className={classNames(
                                'min-w-full flex justify-start items-center gap-2 px-0',
                                isResending && selectedEmail?.id === email.id
                                  ? 'hover:cursor-default opacity-75'
                                  : 'hover:cursor-pointer',
                              )}
                              onClick={(e) => {
                                e.stopPropagation();
                                setRecipient(email.receiver);
                                setSelectedEmail(email);
                                resend(email, email.receiver);
                              }}
                            >
                              <Typography className="flex justify-center justify-items-center gap-1" type="text-sm">
                                Resend
                              </Typography>
                            </div>
                            <div
                              className="min-w-full flex justify-start items-center gap-2 px-0"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedEmail(email);
                                setIsResendModalOpen(true);
                              }}
                            >
                              <Typography className="flex justify-center justify-items-center gap-1" type="text-sm">
                                Resend to another email
                              </Typography>{' '}
                            </div>
                          </Dropdown>
                        </div>
                      </td>
                    </tr>
                    {email.id === expandedId && (
                      <tr className={classNames('email-archive-detail', { expanded: expandedId === email.id })}>
                        <td colSpan={100}>
                          <div className="p-2 pt-0 flex flex-column gap-2">
                            <Card className="w-full">
                              <div className="flex gap-2">
                                <Typography type="text-sm" weight="medium">
                                  Subject:
                                </Typography>
                                <Typography type="text-sm">{email.subject}</Typography>
                              </div>
                              <div
                                className={classNames('email-archive-body mt-4', { expanded: expandedId === email.id })}
                              >
                                {renderBody(email.body)}
                              </div>
                            </Card>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>

          <Paginator
            pagination={emailArchives?.pagination}
            request={searchRequest}
            setRequest={(pag) => {
              setSearchRequest((req) => {
                return { ...req, ...pag };
              });
            }}
          />
        </div>
      </OrderSection>

      {isResendModalOpen && (
        <Modal
          isOpen={isResendModalOpen}
          title="Resend Email"
          actions={
            <>
              <Button
                className="button-secondary"
                onClick={() => {
                  setIsResendModalOpen(false);
                  setSelectedEmail(undefined);
                }}
              >
                Cancel
              </Button>
              <Button
                className="button-yellow"
                onClick={() => {
                  setRecipient(recipient);
                  selectedEmail && resend(selectedEmail, recipient);
                  setIsResendModalOpen(false);
                }}
                data-cy="resend_email"
              >
                Resend
              </Button>
            </>
          }
          close={() => {
            setIsResendModalOpen(false);
            setSelectedEmail(undefined);
          }}
        >
          <div className="min-w-full w-[370px] flex flex-col gap-2 items-stretch">
            <Typography type="text-sm" className="text-[var(--grey-700)]">
              Email Address
            </Typography>
            <input
              className=" h-[44px] border border-[var(--grey-300)] rounded-md px-4"
              type="email"
              value={recipient}
              onChange={(e) => setRecipient(e.target.value)}
            />
          </div>
        </Modal>
      )}
    </>
  );
};
